import { splitGroupsAtReelSections } from './reelTranscriptRows';

import type { ReelSplitGroup } from './reelTranscriptRows';
import type { ReelWordStamp } from '@src/services/reel/reelTranscriptStructure';

/**
 * PLAT-11162 — preparing the translated view's paragraph groups for the reel
 * rows.
 *
 * The translated view renders per PHRASE: each paragraph group `gi` renders
 * `phrasesByGroup[gi]`, a parallel array, and a phrase is one indivisible
 * rendered unit. A Section boundary can still fall inside a paragraph, so the
 * group's source elements are split exactly as the original view splits them —
 * and then each of the group's phrases follows the slice that contains its
 * lead element, which is the phrase-level split the view can actually render.
 * A slice left with no phrases renders nothing and is dropped, the same rule
 * the unsplit view already applies per group.
 */

type TranslatedGroupLike = {
  paragraphId: string | undefined;
  speakerLabel: string | undefined;
  startTime: number | undefined;
  elements: Array<{
    element: {
      type?: string;
      state?: string;
      value?: string | null;
      ts?: number | null;
      initial_index?: number;
    };
    index: number;
  }>;
};

type PhraseLike = {
  /** The phrase's first kept element's `initial_index` — inside this group. */
  lead: number;
};

export type TranslatedReelGroup<TGroup, TPhrase> = ReelSplitGroup<
  TGroup & { groupKey: string }
> & {
  phrases: TPhrase[];
};

export function buildTranslatedReelGroups<
  TGroup extends TranslatedGroupLike,
  TPhrase extends PhraseLike,
>(
  groups: readonly TGroup[],
  phrasesByGroup: readonly (readonly TPhrase[])[],
  stamp: ReelWordStamp,
): TranslatedReelGroup<TGroup, TPhrase>[] {
  const result: TranslatedReelGroup<TGroup, TPhrase>[] = [];

  groups.forEach((group, gi) => {
    const phrases = phrasesByGroup[gi] ?? [];
    if (phrases.length === 0) return;

    const keyed = {
      ...group,
      groupKey: `t-${gi}-${group.paragraphId ?? 'noid'}`,
    };
    const slices = splitGroupsAtReelSections([keyed], stamp);

    if (slices.length === 1) {
      result.push({ ...slices[0], phrases: [...phrases] });
      return;
    }

    // Distribute the group's phrases to the slice holding each phrase's lead
    // element. The lead is the phrase's first kept element and phrase-to-group
    // distribution keyed the phrase to the group that element lives in, so it
    // is in one of these slices; a phrase whose lead resolves nowhere (edge
    // projections) stays with the first slice rather than vanishing.
    const sliceInitialIndexes = slices.map(
      slice =>
        new Set(
          slice.elements.map(({ element }) => element.initial_index ?? -1),
        ),
    );
    const phrasesBySlice: TPhrase[][] = slices.map(() => []);
    for (const phrase of phrases) {
      const at = sliceInitialIndexes.findIndex(indexes =>
        indexes.has(phrase.lead),
      );
      phrasesBySlice[at === -1 ? 0 : at].push(phrase);
    }

    slices.forEach((slice, si) => {
      if (phrasesBySlice[si].length === 0) return;
      result.push({ ...slice, phrases: phrasesBySlice[si] });
    });
  });

  return result;
}
